import { Controller } from '@hotwired/stimulus';
import Shepherd from "shepherd.js";
import "shepherd.js/dist/css/shepherd.css";

const TOTAL_STEPS = 4;

export default class extends Controller {
  static targets = ['newEventButtonDesktop', 'newEventButtonMobile', 'addVisitorLink', 'squadPlanner'];
  static values = {
    tutorialStatus: { type: String, default: 'not_started' },
    calendarId: { type: String }
  }

  connect() {
    this.squadPlannerController = this.application.getControllerForElementAndIdentifier(this.squadPlannerTarget, 'squad-planner');
    this.squadPlannerMenuController = this.application.getControllerForElementAndIdentifier(this.squadPlannerTarget, 'floating-container');
    const isMobile = window.matchMedia("(max-width: 767px)").matches;

    this.tour = new Shepherd.Tour({
      defaultStepOptions: {
        scrollTo: { behavior: 'smooth', block: 'center' },
        classes: 'bg-white shadow-lg p-4 rounded-lg text-sm',
      },
      useModalOverlay: true
    });

    this.tour.addStep({
      id: "new-event-button",
      text: this.#buildStepsContent(1, 'Add activities', 'Create your own events—or use our advanced search to find the perfect activities for your kids.'),
      attachTo: { element: isMobile ? this.newEventButtonMobileTarget : this.newEventButtonDesktopTarget, on: isMobile ? 'top' : 'right' },
      buttons: [
        { text: "Skip Tutorial", classes: 'btn-blue', action: this.#handleSkipTutorial.bind(this) },
        { text: "Next", classes: 'btn-primary',
          action: () => {
            if (isMobile) this.squadPlannerMenuController.show();
            this.tour.next();
          }
        }
      ]
    });

    this.tour.addStep({
      id: "add-visitor-link",
      text: this.#buildStepsContent(2, 'Add child', 'Create a calendar for each child to keep summer plans organized.'),
      attachTo: { element: this.addVisitorLinkTarget, on: isMobile ? 'bottom' : 'right' },
      buttons: [
        { text: "Skip Tutorial", classes: 'btn-blue', action: this.#handleSkipTutorial.bind(this) },
        { text: "Next",
          classes: 'btn-primary',
          action: () => {
            if (isMobile) this.squadPlannerMenuController.hide();
            this.tour.next();
            this.#addDummyEvent();
          }
        }
      ]
    });

    this.tour.addStep({
      id: "calendar",
      text: this.#buildStepsContent(3, 'Last day of school', 'Add your child’s school to see the exact last day and first day back—our complete 2025-26 No School Calendars drop in August.'),
      attachTo: { element: "td.fc-day", on: "bottom" },
      buttons: [
        { text: "Skip Tutorial", classes: 'btn-blue', action: this.#handleSkipTutorial.bind(this) },
        { text: "Next", classes: 'btn-primary', action: this.tour.next }
      ]
    });

    this.tour.addStep({
      id: "account-menu",
      text: this.#buildStepsContent(4, 'Reminder', 'For a more personalized experience, complete your profile at any time.'),
      attachTo: { element: "div.account-menu", on: "bottom" },
      buttons: [
        { text: "Start Planning", classes: "btn-primary", action: this.#handleCompleteTutorial.bind(this) }
      ]
    });

    if (this.tutorialStatusValue === 'not_started') this.tour.start();
  }

  playTutorial() {
    const isMobile = window.matchMedia("(max-width: 767px)").matches;
    if (isMobile && this.squadPlannerMenuController) {
      this.squadPlannerMenuController.hide();
    }
    if (this.tour) this.tour.start();
  }

  #addDummyEvent() {
    if (!this.squadPlannerController) return;
    this.squadPlannerController.addEvent({
      id: this.calendarIdValue,
      title: 'Child 1 - Last Day of School',
      start: '2025-06-01T08:00:00Z',
      allDay: true,
      classNames: ['fc-school-event'],
      extendedProps: {
        context_type: 'School'
      }
    });
  }

  #removeDummyEvent() {
    if (!this.squadPlannerController) return;
    this.squadPlannerController.removeEvent(this.calendarIdValue);
  }

  #buildStepsContent(currentStep, title, text) {
    const dots = Array.from({ length: TOTAL_STEPS }, (_, i) => {
      const isActive = (i+1) === currentStep
      return `<div class="w-3 h-3 rounded-full ${isActive ? 'bg-blue-400' : 'bg-blue-100'}"></div>`
    }).join("");

    return `
      <div class="flex gap-2 mb-4">${dots}</div>
      <div class="font-bold text-blue-400 text-20 !mb-2">${title}</div>
      ${text}
    `;
  }

  #handleSkipTutorial() {
    this.#removeDummyEvent();
    if (this.tour) this.tour.complete();
    if (this.tutorialStatusValue !== 'completed' && this.tutorialStatusValue !== 'skipped') this.#updateTutorialStatus('skipped');
  }

  #handleCompleteTutorial() {
    this.#removeDummyEvent();
    if (this.tour) this.tour.complete();
    if (this.tutorialStatusValue !== 'completed') this.#updateTutorialStatus('completed');
  }


  #updateTutorialStatus(status) {
    if (!this.calendarIdValue) return;

    fetch(`/calendars/${this.calendarIdValue}/update_tutorial_status`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("[name='csrf-token']").content
      },
      body: JSON.stringify({ tutorial_status: status })
    })
  }
}
